import React from 'react';
import { Field } from 'react-final-form';
import isEqual from 'lodash/isEqual';

import { ProductCategoryFragment } from '@occc/graphql';
import { useGetProductCategoriesLazy } from '@occc-app/dataAccessLayer/productCategory/queries';
import {
  AutocompleteProps,
  FormInputProps,
  LazyAutocomplete,
  showErrorOnBlurOrDirty,
} from '@occc-app/design-system';

interface ProductCategorySelectProps extends Pick<AutocompleteProps, 'onFocus' | 'onBlur'> {
  value: ProductCategoryFragment[];
  onChange: (values: ProductCategoryFragment[]) => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  errorText?: string;
  inputProps?: FormInputProps;
}

const mapQueryResponseToRawArray = (
  response: ReturnType<typeof useGetProductCategoriesLazy>['response'],
) => response.data || [];

const mapRawResponseItemToAutocompleteOption = (ProductCategory: ProductCategoryFragment) => ({
  value: ProductCategory.id,
  label: ProductCategory.name,
  _raw: ProductCategory,
});

const ProductCategorySelect: React.FC<ProductCategorySelectProps> = (props) => {
  return (
    <LazyAutocomplete<
      ProductCategoryFragment,
      ReturnType<typeof useGetProductCategoriesLazy>['response']
    >
      label='Product Preferences'
      {...props}
      useLazyQuery={useGetProductCategoriesLazy}
      mapQueryResponseToRawArray={mapQueryResponseToRawArray}
      mapRawResponseItemToAutocompleteOption={mapRawResponseItemToAutocompleteOption}
      placeholder='Start typing to select product preferences'
      excludeSelectedItems
    />
  );
};

interface ProductCategorySelectFieldProps
  extends Omit<ProductCategorySelectProps, 'value' | 'onChange'> {
  name: string;
}

export const ProductCategorySelectField: React.FC<ProductCategorySelectFieldProps> = (props) => {
  return (
    <Field
      name={props.name}
      isEqual={isEqual}
      render={({ input, meta }) => (
        <ProductCategorySelect
          {...props}
          value={input.value || []}
          onFocus={input.onFocus}
          onBlur={input.onBlur}
          onChange={input.onChange}
          errorText={showErrorOnBlurOrDirty({ meta }) ? meta.error : undefined}
        />
      )}
    />
  );
};
